/**
 * Currency Symbol Wrapper
 * Wraps currency symbols in prices with a span for custom styling
 */

class CurrencySymbolWrapper {
  constructor() {
    this.priceSelectors = [
      '.price-item',
      '.wt-product__price__final',
      '.wt-product__price__compare',
      '.cart-item__price',
      '.wt-cart__subtotal__value',
      '.wt-dot__price',
      '.card__price',
      '.unit-price',
      '[class*="price"]'
    ];
    
    // Common currency symbols
    this.currencySymbols = ['$', '€', '£', '¥', '₹', '₽', '₩', 'kr', 'R$', 'CHF'];
    
    this.processedElements = new WeakSet();
    this.init();
  }

  init() {
    // Initial processing
    this.wrapAllPrices();
    
    // Watch for dynamic content changes (cart updates, variant changes, etc.)
    this.observeDOMChanges();
    
    // Listen for Shopify cart updates
    if (typeof Shopify !== 'undefined' && Shopify.theme) {
      document.addEventListener('shopify:section:load', () => this.wrapAllPrices());
    }
  }

  wrapAllPrices() {
    // Use a Set to avoid processing the same element multiple times
    const allPriceElements = new Set();
    
    this.priceSelectors.forEach(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => allPriceElements.add(el));
      } catch (e) {
        // Invalid selector, skip
      }
    });

    allPriceElements.forEach(element => {
      if (!this.processedElements.has(element)) {
        this.wrapCurrencySymbol(element);
        this.processedElements.add(element);
      }
    });
  }

  wrapCurrencySymbol(element) {
    // Skip if already processed or has children with currency-symbol class
    if (element.querySelector('.currency-symbol')) {
      return;
    }

    // Get all text nodes
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      // Only process direct text nodes that contain currency symbols
      if (node.parentElement === element && node.textContent.trim()) {
        textNodes.push(node);
      }
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      let modified = false;
      
      // Check for each currency symbol
      this.currencySymbols.forEach(symbol => {
        if (text.includes(symbol)) {
          // Create a regex to match the currency symbol
          const regex = new RegExp(`(${this.escapeRegex(symbol)})`, 'g');
          const parts = text.split(regex);
          
          if (parts.length > 1) {
            const fragment = document.createDocumentFragment();
            
            parts.forEach(part => {
              if (part === symbol) {
                // Wrap currency symbol in span
                const span = document.createElement('span');
                span.className = 'currency-symbol';
                span.textContent = part;
                fragment.appendChild(span);
              } else if (part) {
                // Keep other text as is
                fragment.appendChild(document.createTextNode(part));
              }
            });
            
            textNode.parentNode.replaceChild(fragment, textNode);
            modified = true;
          }
        }
      });
    });
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  observeDOMChanges() {
    // Create a mutation observer to watch for new price elements
    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;
      
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node or its children contain price elements
              const hasPriceElements = this.priceSelectors.some(selector => {
                try {
                  return node.matches && node.matches(selector) || 
                         node.querySelector && node.querySelector(selector);
                } catch (e) {
                  return false;
                }
              });
              
              if (hasPriceElements) {
                shouldProcess = true;
              }
            }
          });
        }
      });
      
      if (shouldProcess) {
        // Debounce the processing
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.wrapAllPrices();
        }, 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CurrencySymbolWrapper();
  });
} else {
  new CurrencySymbolWrapper();
}
